import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  FileJson,
  FileImage,
  FileText,
  FileVideo,
  Folder,
  FolderOpen,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../api";
import { ShowOperationLauncher } from "../components/ShowOperationLauncher";
import { ShowStoryHeader } from "../components/ShowStoryHeader";
import type {
  ShowArtifact,
  ShowArtifactTreeEntry,
  ShowArtifactTreeResponse,
  ShowArtifactType,
  DeleteShowArtifactsResponse,
  ShowEngine,
  ShowStory,
} from "../show-generator";
import type { Session } from "../types";

const ROOT_PREFIX = "";

export function ShowArtifactsPage({ session }: { session: Session }) {
  const { storyId = "" } = useParams();
  const [story, setStory] = useState<ShowStory | null>(null);
  const [engine, setEngine] = useState<ShowEngine | null>(null);
  const [children, setChildren] = useState<Record<string, ShowArtifactTreeEntry[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [folderLoading, setFolderLoading] = useState<Set<string>>(new Set());
  const [folderErrors, setFolderErrors] = useState<Record<string, string>>({});
  const [selectedEntry, setSelectedEntry] = useState<Extract<ShowArtifactTreeEntry, { kind: "file" }> | null>(null);
  const [artifact, setArtifact] = useState<ShowArtifact | null>(null);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifactError, setArtifactError] = useState("");
  const [pageError, setPageError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<Map<string, string>>(new Map());
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [hasActiveOperation, setHasActiveOperation] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const folderRequests = useRef(new Set<string>());

  const loadFolder = useCallback(async (prefix: string, force = false) => {
    if (!force && (children[prefix] || folderRequests.current.has(prefix))) return;
    folderRequests.current.add(prefix);
    setFolderLoading((current) => new Set(current).add(prefix));
    setFolderErrors((current) => {
      const next = { ...current };
      delete next[prefix];
      return next;
    });
    try {
      const query = prefix ? `?parent=${encodeURIComponent(prefix)}` : "";
      const result = await apiRequest<ShowArtifactTreeResponse>(
        `/api/show-generator/stories/${storyId}/artifacts/tree${query}`,
      );
      setChildren((current) => ({ ...current, [prefix]: result.entries }));
    } catch (failure) {
      setFolderErrors((current) => ({
        ...current,
        [prefix]: failure instanceof Error ? failure.message : "Could not load this folder",
      }));
    } finally {
      folderRequests.current.delete(prefix);
      setFolderLoading((current) => {
        const next = new Set(current);
        next.delete(prefix);
        return next;
      });
    }
  }, [children, storyId]);

  useEffect(() => {
    let active = true;
    setPageError("");
    setStory(null);
    setEngine(null);
    setChildren({});
    setExpanded(new Set());
    setSelectedEntry(null);
    setArtifact(null);
    setSelectedFiles(new Map());
    setSelectedFolders(new Set());
    setHasActiveOperation(true);
    folderRequests.current.clear();
    void apiRequest<ShowStory>(`/api/show-generator/stories/${storyId}`).then(async (currentStory) => {
      const [definition, root] = await Promise.all([
        apiRequest<ShowEngine>(`/api/show-generator/engines/${encodeURIComponent(currentStory.engine)}`),
        apiRequest<ShowArtifactTreeResponse>(`/api/show-generator/stories/${storyId}/artifacts/tree`),
      ]);
      if (!active) return;
      setStory(currentStory);
      setEngine(definition);
      setChildren({ [ROOT_PREFIX]: root.entries });
    }).catch((failure) => {
      if (active) setPageError(failure instanceof Error ? failure.message : "Could not load artifacts");
    });
    return () => { active = false; };
  }, [storyId]);

  useEffect(() => {
    if (!selectedEntry) return;
    let active = true;
    setArtifactLoading(true);
    setArtifactError("");
    setArtifact(null);
    void apiRequest<ShowArtifact>(
      `/api/show-generator/stories/${storyId}/artifacts/${selectedEntry.id}`,
    ).then((result) => {
      if (active) setArtifact(result);
    }).catch((failure) => {
      if (active) setArtifactError(failure instanceof Error ? failure.message : "Could not load this artifact");
    }).finally(() => {
      if (active) setArtifactLoading(false);
    });
    return () => { active = false; };
  }, [selectedEntry, storyId]);

  const refresh = useCallback(() => {
    const prefixes = [ROOT_PREFIX, ...expanded];
    setChildren({});
    setFolderErrors({});
    setSelectedEntry(null);
    setArtifact(null);
    for (const prefix of prefixes) void loadFolder(prefix, true);
  }, [expanded, loadFolder]);

  function toggleFolder(prefix: string) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
    if (!expanded.has(prefix)) void loadFolder(prefix);
  }

  function toggleSelection(entry: ShowArtifactTreeEntry) {
    if (entry.kind === "folder") {
      const next = new Set(selectedFolders);
      if (next.has(entry.prefix)) {
        next.delete(entry.prefix);
      } else {
        for (const prefix of next) if (prefix.startsWith(`${entry.prefix}.`)) next.delete(prefix);
        next.add(entry.prefix);
        setSelectedFiles((files) => new Map([...files].filter(([, key]) => !key.startsWith(`${entry.prefix}.`))));
      }
      setSelectedFolders(next);
      return;
    }
    if (coveredByFolder(entry.key, selectedFolders)) return;
    setSelectedFiles((current) => {
      const next = new Map(current);
      if (next.has(entry.id)) next.delete(entry.id);
      else next.set(entry.id, entry.key);
      return next;
    });
  }

  async function deleteSelected() {
    setDeleting(true);
    setDeleteError("");
    try {
      const result = await apiRequest<DeleteShowArtifactsResponse>(
        `/api/show-generator/stories/${storyId}/artifacts`,
        { method: "DELETE", body: JSON.stringify({ artifactIds: [...selectedFiles.keys()], prefixes: [...selectedFolders] }) },
        session.csrfToken,
      );
      const removedIds = new Set(result.deleted.map((item) => item.id));
      if (selectedEntry && removedIds.has(selectedEntry.id)) {
        setSelectedEntry(null);
        setArtifact(null);
      }
      setSelectedFiles(new Map());
      setSelectedFolders(new Set());
      setConfirmDelete(false);
      const currentStory = await apiRequest<ShowStory>(`/api/show-generator/stories/${storyId}`);
      setStory(currentStory);
      refresh();
    } catch (failure) {
      setDeleteError(failure instanceof Error ? failure.message : "Could not delete selected artifacts");
    } finally {
      setDeleting(false);
    }
  }

  const selectedCount = selectedFiles.size + selectedFolders.size;

  return <>
    {!story ? <div className="loading-state">Loading story…</div> : null}
    {pageError ? <div className="error-banner" role="alert">{pageError}</div> : null}
    {story && engine ? <ShowOperationLauncher story={story} engine={engine} session={session} onActiveChange={setHasActiveOperation} onOperationFinished={refresh} renderHeader={(controls) => <ShowStoryHeader story={story} actions={controls} />} /> : story ? <ShowStoryHeader story={story} /> : null}
    {deleteError ? <div className="error-banner" role="alert">{deleteError}</div> : null}
    <section className="artifact-browser" aria-label="Story artifacts">
      <aside className="artifact-tree-pane" aria-label="Artifact folders">
        <header className="artifact-pane-header">
          <div><h2>Artifacts</h2><p>Dot-separated artifact keys</p></div>
          <div className="artifact-pane-actions">
            <button className="danger-action artifact-delete-button" type="button" disabled={!selectedCount || hasActiveOperation || deleting} title={hasActiveOperation ? "Delete is unavailable while an operation is active" : "Delete selected artifacts"} onClick={() => setConfirmDelete(true)}>
              <Trash2 size={15} />{selectedCount ? `Delete selected (${selectedCount})` : "Delete selected"}
            </button>
            <button className="icon-button" type="button" title="Refresh artifact tree" onClick={refresh}><RefreshCw size={16} /></button>
          </div>
        </header>
        <div className="artifact-tree" role="tree">
          <TreeLevel
            prefix={ROOT_PREFIX}
            depth={0}
            childrenByPrefix={children}
            expanded={expanded}
            loading={folderLoading}
            errors={folderErrors}
            selectedId={selectedEntry?.id ?? null}
            selectedFiles={selectedFiles}
            selectedFolders={selectedFolders}
            onToggle={toggleFolder}
            onSelect={setSelectedEntry}
            onToggleSelection={toggleSelection}
            onRetry={(prefix) => void loadFolder(prefix, true)}
          />
        </div>
      </aside>
      <main className="artifact-view-pane" aria-live="polite">
        {!selectedEntry ? <ArtifactPlaceholder /> : artifactLoading ? <div className="loading-state">Loading artifact…</div> : artifactError ? <div className="artifact-view-error" role="alert"><AlertCircle size={18} /><span>{artifactError}</span></div> : artifact ? <ArtifactViewer artifact={artifact} /> : null}
      </main>
    </section>
    {confirmDelete ? <div className="modal-backdrop"><section className="confirm-modal artifact-delete-modal" role="dialog" aria-modal="true" aria-labelledby="delete-artifacts-title">
      <header><div><p className="eyebrow">Destructive action</p><h2 id="delete-artifacts-title">Delete selected artifacts</h2></div><button className="icon-button" type="button" onClick={() => setConfirmDelete(false)} title="Cancel delete"><X size={18} /></button></header>
      <p>Selected folders are deleted recursively. Artifacts in later steps are retained but may become stale.</p>
      <ul>{[...selectedFolders].map((prefix) => <li key={prefix}><Folder size={14} />{prefix}</li>)}{[...selectedFiles].map(([id, key]) => <li key={id}><FileText size={14} />{key}</li>)}</ul>
      <footer><button className="secondary-button" type="button" onClick={() => setConfirmDelete(false)}>Cancel</button><button className="danger-button" type="button" disabled={deleting || hasActiveOperation} onClick={() => void deleteSelected()}><Trash2 size={15} />{deleting ? "Deleting…" : "Confirm delete"}</button></footer>
    </section></div> : null}
  </>;
}

interface TreeLevelProps {
  prefix: string;
  depth: number;
  childrenByPrefix: Record<string, ShowArtifactTreeEntry[]>;
  expanded: Set<string>;
  loading: Set<string>;
  errors: Record<string, string>;
  selectedId: string | null;
  selectedFiles: Map<string, string>;
  selectedFolders: Set<string>;
  onToggle: (prefix: string) => void;
  onSelect: (entry: Extract<ShowArtifactTreeEntry, { kind: "file" }>) => void;
  onToggleSelection: (entry: ShowArtifactTreeEntry) => void;
  onRetry: (prefix: string) => void;
}

function TreeLevel(props: TreeLevelProps) {
  const entries = props.childrenByPrefix[props.prefix];
  if (props.loading.has(props.prefix) && !entries) {
    return <div className="artifact-tree-message" style={{ paddingLeft: 12 + props.depth * 16 }}>Loading…</div>;
  }
  if (props.errors[props.prefix] && !entries) {
    return <button className="artifact-tree-error" type="button" onClick={() => props.onRetry(props.prefix)} style={{ marginLeft: 8 + props.depth * 16 }}><AlertCircle size={14} />Retry folder</button>;
  }
  if (!entries?.length) {
    return props.depth === 0 ? <div className="artifact-tree-message">No artifacts</div> : null;
  }

  return <>{entries.map((entry) => entry.kind === "folder" ? <FolderNode key={entry.prefix} entry={entry} {...props} /> : <div className="artifact-tree-entry" key={entry.id} style={{ paddingLeft: 8 + props.depth * 16 }}>
    <input type="checkbox" aria-label={`Select file ${entry.key}`} checked={props.selectedFiles.has(entry.id) || coveredByFolder(entry.key, props.selectedFolders)} disabled={coveredByFolder(entry.key, props.selectedFolders)} onChange={() => props.onToggleSelection(entry)} />
    <button type="button" role="treeitem" aria-selected={props.selectedId === entry.id} className={`artifact-tree-row artifact-file-row${props.selectedId === entry.id ? " active" : ""}`} onClick={() => props.onSelect(entry)} title={entry.key}>
      <span className="tree-spacer" aria-hidden="true" /><ArtifactIcon type={entry.type} /><span>{entry.name}</span><span className={`artifact-validity-dot ${entry.validity}`} title={entry.validity} />
    </button>
  </div>)}</>;
}

function FolderNode({ entry, ...props }: { entry: Extract<ShowArtifactTreeEntry, { kind: "folder" }> } & TreeLevelProps) {
  const isExpanded = props.expanded.has(entry.prefix);
  const inherited = [...props.selectedFolders].some((prefix) => entry.prefix.startsWith(`${prefix}.`));
  return <div role="none">
    <div className="artifact-tree-entry" style={{ paddingLeft: 8 + props.depth * 16 }}>
    <input type="checkbox" aria-label={`Select folder ${entry.prefix}`} checked={props.selectedFolders.has(entry.prefix) || inherited} disabled={inherited} onChange={() => props.onToggleSelection(entry)} />
    <button
      type="button"
      role="treeitem"
      aria-expanded={isExpanded}
      className="artifact-tree-row artifact-folder-row"
      onClick={() => props.onToggle(entry.prefix)}
      title={entry.prefix}
    >
      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      {isExpanded ? <FolderOpen size={16} /> : <Folder size={16} />}
      <span>{entry.name}</span>
      {props.loading.has(entry.prefix) ? <RefreshCw className="spin tree-row-progress" size={13} /> : null}
    </button>
    </div>
    {isExpanded ? <div role="group"><TreeLevel {...props} prefix={entry.prefix} depth={props.depth + 1} /></div> : null}
  </div>;
}

function coveredByFolder(key: string, folders: Set<string>): boolean {
  return [...folders].some((prefix) => key.startsWith(`${prefix}.`));
}

function ArtifactIcon({ type }: { type: ShowArtifactType }) {
  if (type === "json") return <FileJson size={16} aria-hidden="true" />;
  if (type === "text") return <FileText size={16} aria-hidden="true" />;
  if (type === "image-url") return <FileImage size={16} aria-hidden="true" />;
  return <FileVideo size={16} aria-hidden="true" />;
}

function ArtifactPlaceholder() {
  return <div className="artifact-selection-empty"><FolderOpen size={30} /><h2>Select an artifact</h2><p>Expand a folder and choose a file to inspect its metadata and content.</p></div>;
}

function ArtifactViewer({ artifact }: { artifact: ShowArtifact }) {
  return <>
    <header className="artifact-detail-header">
      <div>
        <p className="eyebrow">{artifact.type}</p>
        <h2>{artifact.key}</h2>
        <code>{artifact.id}</code>
      </div>
      <span className={`status-badge ${artifact.validity === "valid" ? "status-success" : "status-warning"}`}>{artifact.validity}</span>
    </header>
    {artifact.staleReasons.length ? <div className="artifact-stale-reasons" role="status"><AlertCircle size={16} /><div><strong>Stale dependencies</strong><p>{artifact.staleReasons.join("; ")}</p></div></div> : null}
    <div className="artifact-content-view">{renderArtifactContent(artifact)}</div>
  </>;
}

function renderArtifactContent(artifact: ShowArtifact) {
  if (artifact.type === "json") {
    let value: unknown = artifact.data;
    try { value = JSON.parse(artifact.data); } catch { /* Show malformed persisted data verbatim. */ }
    return <pre className="artifact-data">{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</pre>;
  }
  if (artifact.type === "text") return <pre className="artifact-data">{artifact.data}</pre>;
  if (!artifact.signedUrl) return <div className="empty-state">This media artifact has no signed URL.</div>;

  const filename = artifact.key.split(".").at(-1) || "artifact";
  return <div className="media-preview">
    {artifact.type === "image-url" ? <img src={artifact.signedUrl} alt={artifact.key} /> : <video src={artifact.signedUrl} controls preload="metadata" />}
    <div className="artifact-media-actions">
      <a className="secondary-button" href={artifact.signedUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} />Open</a>
      <a className="secondary-button" href={artifact.signedUrl} download={filename}><Download size={15} />Download</a>
    </div>
  </div>;
}
